import { useMemo, useState } from 'react'
import { useQuery } from '@tanstack/react-query'
import { useDebounce } from 'use-debounce'

import useApiService from 'services/api'
import { useUsers } from 'services/users'
import { EmailRecipientOption } from 'components/FormUtils/EmailRecipientSelect'
import { EmailContact } from 'models/emailIntegration'

/** Re-exported so callers of the hook don't need a second import path. */
export type RecipientOption = EmailRecipientOption

interface UseRecipientOptionsResult {
  options: RecipientOption[]
  isLoading: boolean
  /** Forward to `onSearchChange` on the select component. */
  setSearch: (value: string) => void
}

// Stable reference so callers who omit `extraContacts` don't thrash the memo.
const EMPTY_CONTACTS: EmailContact[] = []

/**
 * Builds the autocomplete options for an email recipient select by merging
 * team users, distinct senders, and distinct recipients from email history,
 * plus any thread-context contacts the caller wants surfaced unconditionally.
 *
 * Options are `{label: "Name <email>" | email, value: email}`. Dedupes by email.
 *
 * `enableEmailHistory` toggles the inbox-only `getDistinctSenders`/
 * `getDistinctRecipients` queries. Campaign contexts (no inbox integration)
 * should pass `false` to skip the two no-op requests.
 */
export function useRecipientOptions(
  extraContacts: EmailContact[] = EMPTY_CONTACTS,
  enableEmailHistory: boolean = true
): UseRecipientOptionsResult {
  const api = useApiService()
  const [search, setSearch] = useState('')
  const [debouncedSearch] = useDebounce(search, 300)

  const { data: users } = useUsers(undefined, { query: { enabled: true } })

  const { data: senderContacts, isLoading: loadingSenders } = useQuery({
    queryKey: ['distinct-senders', debouncedSearch],
    queryFn: () =>
      api.emailIntegration.getDistinctSenders({ search: debouncedSearch }),
    enabled: enableEmailHistory,
  })
  const { data: recipientContacts, isLoading: loadingRecipients } = useQuery({
    queryKey: ['distinct-recipients', debouncedSearch],
    queryFn: () =>
      api.emailIntegration.getDistinctRecipients({ search: debouncedSearch }),
    enabled: enableEmailHistory,
  })

  const isLoading =
    search !== debouncedSearch || loadingSenders || loadingRecipients

  const options = useMemo(() => {
    const seen = new Set<string>()
    const result: RecipientOption[] = []

    const addOption = (email: string, name: string) => {
      const key = email.toLowerCase()
      if (seen.has(key)) return
      seen.add(key)
      result.push({
        label: name ? `${name} <${email}>` : email,
        value: email,
      })
    }

    users
      ?.filter((u) => u.is_active && u.email)
      .forEach((u) =>
        addOption(u.email, `${u.first_name} ${u.last_name}`.trim())
      )

    senderContacts?.forEach((c) => addOption(c.email, c.name))
    recipientContacts?.forEach((c) => addOption(c.email, c.name))

    extraContacts.forEach((c) => addOption(c.email, c.name))

    return result
  }, [users, senderContacts, recipientContacts, extraContacts])

  return { options, isLoading, setSearch }
}
